import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'
import type * as React from 'react'

const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs-600 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-bg3 text-primary',
        // Category/flag labels (channel types, "Default", capabilities). Mono
        // label so a tag stops out-weighting the row name it annotates (the
        // base 600 weight was heavier than the 500 the first column uses) and
        // raw API values read as labels once uppercased.
        // Filled, NOT outlined: every button in this kit is a hairline border
        // plus mono uppercase, so an outlined mono chip is indistinguishable
        // from one. The fill is what keeps a tag reading as a label.
        // bg-hv1, not bg-bg3 (the `default` fill): bg3 is a mid grey in dark
        // (40% L) and would make a passive tag the brightest thing in the row.
        // px-2, not the base px-2.5: the 0.14em tracking already adds trailing
        // space after the last glyph.
        tag: 'border-transparent bg-hv1 px-2 font-jetbrains text-label uppercase text-secondary',
        count:
          'border-transparent bg-hv1 px-2 font-jetbrains text-xs-400 tabular-nums normal-case tracking-normal text-foreground hover:bg-hv2',
        secondary: 'border-transparent bg-bg3 text-secondary hover:bg-hv2',
        destructive: 'border-transparent bg-status-danger/[0.12] text-status-danger',
        outline: 'text-foreground',
        // dark: pair mirrors `warning`/`orange`/`blue` below. Without it the
        // emerald-100 fill is theme-blind and the Active tag renders a near-white
        // chip on the dark page.
        // A 12% wash of the mark token with the mark as text — the same
        // language as Alert, and no dark: pair to maintain, because --status-*
        // is vivid on both grounds. These replaced stock emerald/amber/orange
        // fills that each carried a hand-written dark variant.
        success: 'border-transparent bg-status-success/[0.12] text-status-success',
        danger: 'bg-destructive text-destructive-foreground border-none',
        disabled: 'bg-bg3 text-tertiary border-none',
        warning: 'border-transparent bg-status-warning/[0.12] text-status-warning',
        progress: 'border-transparent bg-progress text-progress-foreground',
        orange: 'border-transparent bg-status-warning/[0.12] text-status-warning',
        blue: 'border-transparent bg-status-progress/[0.12] text-status-progress',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = ({ className, variant, ...props }: BadgeProps) => (
  <div className={cn(badgeVariants({ variant }), className)} {...props} />
)

export { Badge, badgeVariants }
